import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { resetAllStore, setAccessFalse } from 'Stores/App';
import { userStore } from 'Stores/User';
import { logout } from 'Services/Queries';
import { routePath } from 'Navigator/routes';
import { API_GW_REFRESH_TOKEN_TIMEOUT } from 'Config';

// backendden time out ne ise -> uyarı; 60 sn countdown -> otomatik çıkış

const WARNING_DURATION_S = 60;
const IDLE_LIMIT_MS = Math.max(0, API_GW_REFRESH_TOKEN_TIMEOUT- WARNING_DURATION_S) * 1000;

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'mousemove',
];

//sn mouse hareketi
const MOUSEMOVE_THROTTLE_MS = 1000;

const useSessionTimeout = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { sessionToken, customerNumber, isMiniAppLogin } = useSelector(userStore);

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_DURATION_S);

  const idleTimerRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const isActive = !!sessionToken && !isMiniAppLogin;

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const clearCountdown = () => {
    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  const performLogout = useCallback(() => {
    clearIdleTimer();
    clearCountdown();
    setIsWarningOpen(false);
    try {
      if (sessionToken && customerNumber) {
        logout({ sessionToken, customerNumber });
      }
    } catch {
    }
    dispatch(setAccessFalse());
    dispatch(resetAllStore());
    history.push(routePath.welcome);
  }, [dispatch, history, sessionToken, customerNumber]);

  const startWarning = useCallback(() => {
    setSecondsLeft(WARNING_DURATION_S);
    setIsWarningOpen(true);
    clearCountdown();
    countdownTimerRef.current = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearCountdown();
          performLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [performLogout]);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = window.setTimeout(() => {
      startWarning();
    }, IDLE_LIMIT_MS);
  }, [startWarning]);

  const extendSession = useCallback(() => {
    clearCountdown();
    setIsWarningOpen(false);
    setSecondsLeft(WARNING_DURATION_S);
    lastActivityRef.current = Date.now();
    startIdleTimer();
  }, [startIdleTimer]);

  useEffect(() => {
    if (!isActive) {
      clearIdleTimer();
      clearCountdown();
      setIsWarningOpen(false);
      return;
    }

    const handleActivity = (e: Event) => {

      if (isWarningOpen) return;

      if (e.type === 'mousemove') {
        const now = Date.now();
        if (now - lastActivityRef.current < MOUSEMOVE_THROTTLE_MS) return;
        lastActivityRef.current = now;
      }
      startIdleTimer();
    };

    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, handleActivity, { passive: true });
    });

    startIdleTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, handleActivity);
      });
      clearIdleTimer();
      clearCountdown();
    };
  }, [isActive, isWarningOpen, startIdleTimer]);

  return {
    isWarningOpen,
    secondsLeft,
    extendSession,
    performLogout,
  };
};

export default useSessionTimeout;